import * as SavedReportConstants from "../constants/SavedReportConstants";

const pad = (value) => String(value).padStart(2, "0");

// ReportSchedule.TimeOfDay is minutes after local midnight.
export const formatTimeOfDay = (minutes) =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

export const TIME_OF_DAY_OPTIONS = Array.from(
  { length: (24 * 60) / SavedReportConstants.SCHEDULE_TIME_STEP_MINUTES },
  (_, index) => {
    const minutes = index * SavedReportConstants.SCHEDULE_TIME_STEP_MINUTES;
    return { id: minutes, name: formatTimeOfDay(minutes) };
  }
);

// .NET DayOfWeek: Sunday = 0 ... Saturday = 6. 7 January 2024 was a Sunday.
export const weekdayName = (dayOfWeek, intl) =>
  intl.formatDate(Date.UTC(2024, 0, 7 + dayOfWeek), {
    weekday: "long",
    timeZone: "UTC",
  });

// The API may send UTC times without an offset; read those as UTC.
export const parseUtc = (value) => {
  if (!value) {
    return null;
  }
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasOffset ? value : `${value}Z`);
};

export const formatDateTime = (value, intl) =>
  value
    ? intl.formatDate(parseUtc(value), {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

const formatName = (format) =>
  SavedReportConstants.REPORT_FILE_FORMATS.find((item) => item.id === format)
    ?.name || "excel";

export const describeSchedule = (schedule, intl) => {
  if (!schedule) {
    return intl.formatMessage({ id: "saved_report_not_scheduled" });
  }
  const values = {
    time: formatTimeOfDay(schedule.timeOfDay),
    timezone: schedule.timezone,
    format: intl.formatMessage({ id: formatName(schedule.format) }),
  };
  if (schedule.frequency === SavedReportConstants.SCHEDULE_FREQUENCY_WEEKLY) {
    return intl.formatMessage(
      { id: "schedule_summary_weekly" },
      { ...values, day: weekdayName(schedule.dayOfWeek, intl) }
    );
  }
  return intl.formatMessage(
    { id: "schedule_summary_monthly" },
    { ...values, day: schedule.dayOfMonth }
  );
};

export const describeScheduleState = (schedule, intl) => {
  if (!schedule) {
    return intl.formatMessage({ id: "saved_report_not_scheduled" });
  }
  return intl.formatMessage({
    id: schedule.isPaused
      ? "saved_report_state_paused"
      : "saved_report_state_active",
  });
};

const RESULT_MESSAGES = {
  [SavedReportConstants.REPORT_SEND_RESULT_SENT]: "saved_report_result_sent",
  [SavedReportConstants.REPORT_SEND_RESULT_FAILED]: "saved_report_result_failed",
  [SavedReportConstants.REPORT_SEND_RESULT_SKIPPED]:
    "saved_report_result_skipped",
};

export const describeResult = (result, intl) =>
  RESULT_MESSAGES[result]
    ? intl.formatMessage({ id: RESULT_MESSAGES[result] })
    : "";

// An unfinished send (inProgress) carries a placeholder result (Failed) and
// reason ("Queued" or "In progress") until it ends: show its state instead.
export const describeSendResult = (send, intl) => {
  if (send?.inProgress) {
    return intl.formatMessage({
      id: send.queued
        ? "saved_report_result_queued"
        : "saved_report_result_sending",
    });
  }
  return describeResult(send?.result, intl);
};

// The reason to show under the result: none while the send is unfinished.
export const sendReason = (send) => (send?.inProgress ? null : send?.reason);

export const describeLastSend = (lastSend, intl) =>
  lastSend
    ? intl.formatMessage(
        { id: "saved_report_last_result_value" },
        {
          result: describeSendResult(lastSend, intl),
          time: formatDateTime(lastSend.startedAtUtc, intl),
        }
      )
    : "";
